import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { decodeId } from '../../utils/idCodec';
import { NameCell } from '../../hooks/useNameMaps';
import { Alert, Button, Card, SearchableSelect, StatusBadge, Tabs, useToast } from '@kss/ui';
import { ChevronLeft, ChevronRight, Save, CheckCircle2 } from 'lucide-react';
import type { Employee, EmployeeCreate } from '@kss/api';
import { employeeService, enumEntries } from '@kss/api';
import { loadStoreOptions, loadEmployeeOptions } from '../shared/formLoaders';
import {
  validateAadhaar,
  validateEmail,
  validateMobile,
  validatePan,
} from '../../utils/formatValidators';
import ImagePicker from '../../components/ImagePicker';
import { persistImage } from '../../utils/persistImage';

/**
 * Employee form — fields rewritten 2026-05-02 to match the live API
 * shape exactly (was producing 500 errors because of a `joiningDate`
 * vs `dateOfJoining` mismatch + missing `storeId` FK + extra `salary`,
 * `companyId`, `address` columns the server doesn't have).
 *
 * Live API canonical fields (per swagger):
 *   employeeCode, firstName, lastName, email, phone,
 *   dateOfJoining, dateOfLeaving, department, designation,
 *   status (int enum), panNumber, aadhaarNumber, imageUrl,
 *   storeId (FK, REQUIRED for create), reportingManagerId.
 */

type TabStatus = 'default' | 'danger' | 'success' | 'warning';
type FormState = {
  employeeCode: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  designation: string;
  department: string;
  dateOfJoining: string;
  dateOfLeaving: string;
  panNumber: string;
  aadhaarNumber: string;
  imageUrl: string;
  storeId: string;
  reportingManagerId: string;
  status: number; // EmployeeStatus int enum: 1=Active, 2=OnLeave, 3=Resigned, 4=Terminated
};

const today = new Date().toISOString().slice(0, 10);
const emptyForm: FormState = {
  employeeCode: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  designation: '',
  department: '',
  dateOfJoining: today,
  dateOfLeaving: '',
  panNumber: '',
  aadhaarNumber: '',
  imageUrl: '',
  storeId: '',
  reportingManagerId: '',
  status: 1,
};

const STEPS = ['basic', 'job', 'organisation', 'review'] as const;
type StepId = (typeof STEPS)[number];
const STEP_LABELS: Record<StepId, string> = {
  basic: 'Basic info',
  job: 'Job details',
  organisation: 'Organisation',
  review: 'Review & save',
};

/* Single source of truth — see enums.ts EmployeeStatusLabels. The grid used
   to read 3 as "Terminated" while this form wrote 3 for "Resigned". */
const STATUS_OPTIONS = enumEntries('EmployeeStatus');

const inputCls =
  'h-10 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const selectCls =
  'h-10 w-full appearance-none rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';

export default function NewEmployee() {
  const { id: rawId } = useParams<{ id?: string }>();
  const id = decodeId(rawId);
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const isEdit = !!id && id !== 'new';
  const [form, setForm] = useState<FormState>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [step, setStep] = useState<StepId>('basic');
  const [redTabs, setRedTabs] = useState<Set<StepId>>(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (isEdit && id) {
        setLoading(true);
        setLoadError(null);
        try {
          const rec = await employeeService.get(id);
          if (cancelled) return;
          setForm({
            employeeCode: rec.employeeCode ?? rec.code ?? '',
            firstName: rec.firstName ?? '',
            lastName: rec.lastName ?? '',
            email: rec.email ?? '',
            phone: rec.phone ?? '',
            designation: rec.designation ?? '',
            department: rec.department ?? '',
            dateOfJoining: (rec.dateOfJoining ?? rec.joiningDate)?.slice(0, 10) ?? today,
            dateOfLeaving: rec.dateOfLeaving?.slice(0, 10) ?? '',
            panNumber: rec.panNumber ?? '',
            aadhaarNumber: rec.aadhaarNumber ?? '',
            imageUrl: rec.imageUrl ?? '',
            storeId: rec.storeId ?? '',
            reportingManagerId: rec.reportingManagerId ?? '',
            status: typeof rec.status === 'number' ? rec.status : 1,
          });
        } catch (e) {
          const msg = e instanceof Error ? e.message : 'Not found';
          setLoadError(msg);
          toastRef.current.error('Load failed', msg);
        } finally {
          if (!cancelled) setLoading(false);
        }
      } else setForm(emptyForm);
      setErrors({});
      setStep('basic');
      setRedTabs(new Set());
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const validateStep = (s: StepId): boolean => {
    const errs: typeof errors = {};
    if (s === 'basic' || s === 'review') {
      if (!form.firstName.trim()) errs.firstName = 'First name required';
      // Live API rejects creates without `employeeCode` with a clean
      // `REQUIRED_FIELD:EmployeeCode` (verified 2026-05-05). Block at
      // the form layer so the user gets the inline error rather than
      // a Save Failed toast.
      if (!form.employeeCode.trim()) errs.employeeCode = 'Employee code required';
      const emailErr = validateEmail(form.email);
      if (emailErr) errs.email = emailErr;
      const phoneErr = validateMobile(form.phone);
      if (phoneErr) errs.phone = phoneErr;
    }
    if (s === 'job' || s === 'review') {
      const panErr = validatePan(form.panNumber);
      if (panErr) errs.panNumber = panErr;
      const aadhaarErr = validateAadhaar(form.aadhaarNumber);
      if (aadhaarErr) errs.aadhaarNumber = aadhaarErr;
    }
    if (s === 'organisation' || s === 'review') {
      // storeId is the live API's required FK; without it we get 500.
      if (!form.storeId) errs.storeId = 'Store is required';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const goNext = () => {
    const i = STEPS.indexOf(step);
    if (i >= STEPS.length - 1) return;
    if (!validateStep(step)) {
      toast.error('Fix errors', 'Some fields are invalid');
      return;
    }
    const next = STEPS[i + 1]!;
    setRedTabs((p) => {
      const n = new Set(p);
      n.delete(next);
      return n;
    });
    setStep(next);
  };
  const goPrev = () => {
    const i = STEPS.indexOf(step);
    if (i === 0) return;
    setRedTabs((p) => new Set(p).add(step));
    setStep(STEPS[i - 1]!);
  };

  const basicValid = !!form.firstName.trim();

  const handleSave = async () => {
    // Tab-wise validation: only validate fields visible on the
    // current tab. Save & Next on intermediate tabs just advances —
    // the backend save runs only when the user reaches Review.
    // This means the Store picker on the Organisation tab can't
    // block Save & Next on the Basic tab any more.
    if (!validateStep(step)) {
      toast.error('Fix errors', 'Some fields are invalid');
      return;
    }
    const idx = STEPS.indexOf(step);
    const isLastTab = idx === STEPS.length - 1;
    if (!isLastTab) {
      const next = STEPS[idx + 1]!;
      setRedTabs((p) => {
        const n = new Set(p);
        n.delete(next);
        return n;
      });
      setStep(next);
      return;
    }
    // Final tab (Review) — full validation across every field.
    if (!validateStep('review')) {
      toast.error('Fix errors', 'Some fields are invalid — check the red tabs');
      return;
    }
    if (!form.storeId) {
      toast.error('Store required', 'Pick a store on the Organisation tab — backend rejects employees without one.');
      setStep('organisation');
      return;
    }
    setSaving(true);
    try {
      const body: Employee = {
        employeeId: isEdit ? id : undefined,
        employeeCode: form.employeeCode || undefined,
        firstName: form.firstName,
        lastName: form.lastName || undefined,
        email: form.email || undefined,
        phone: form.phone || undefined,
        designation: form.designation || undefined,
        department: form.department || undefined,
        dateOfJoining: form.dateOfJoining || undefined,
        dateOfLeaving: form.dateOfLeaving || undefined,
        panNumber: form.panNumber ? form.panNumber.toUpperCase() : undefined,
        aadhaarNumber: form.aadhaarNumber ? form.aadhaarNumber.replace(/\s/g, '') : undefined,
        imageUrl: await persistImage(form.imageUrl, 'employee'),
        storeId: form.storeId,
        reportingManagerId: form.reportingManagerId || undefined,
        status: form.status,
      };
      if (isEdit) {
        await employeeService.update([body]);
        toast.success('Saved', `${form.firstName} updated`);
      } else {
        const { employeeId: _o, ...create } = body;
        // Live `POST /api/Employee` returns `data: true` (boolean) — same
        // contract bug as SalesOrder/Store/Product. Recover the id by
        // filtering on `employeeCode` (or `email` when blank) so Save &
        // Next still deep-links. Critically: do NOT `return` when the
        // recovery fails, otherwise the tab-advance code never runs and
        // Save & Next dumps the user back at the list.
        await employeeService.create([create as EmployeeCreate]);
        toast.success('Saved', `${form.firstName} added`);
      }
      // Review-tab save succeeded — back to the list (matches edit mode).
      navigate('/panel/employees');
    } catch (e) {
      toast.error('Save failed', e instanceof Error ? e.message : 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const tabStatus = useMemo<Record<string, TabStatus>>(() => {
    const m: Record<string, TabStatus> = {};
    redTabs.forEach((s) => (m[s] = 'danger'));
    return m;
  }, [redTabs]);

  const field = (
    label: string,
    key: keyof FormState,
    input: React.ReactNode,
    required?: boolean,
  ) => (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-content-secondary">
        {label} {required && <span className="text-danger">*</span>}
      </span>
      {input}
      {errors[key] && <span className="text-xs text-danger">{errors[key]}</span>}
    </label>
  );

  // "blank text box and show alert" UX — onBlur, if format fails,
  // pop an error toast and clear the input.
  const blurValidate = <K extends keyof FormState>(
    key: K,
    label: string,
    validator: (v: string) => string | null,
  ) => (e: React.FocusEvent<HTMLInputElement>) => {
    const err = validator(e.currentTarget.value);
    if (err) {
      toast.error(`Invalid ${label}`, err);
      set(key, '' as FormState[K]);
    }
  };

  const basicStep = (
    <div className="grid gap-3 md:grid-cols-2">
      {field(
        'Employee code',
        'employeeCode',
        <input
          value={form.employeeCode}
          onChange={(e) => set('employeeCode', e.target.value)}
          placeholder="EMP-001"
          className={inputCls}
        />,
      )}
      {field(
        'First name',
        'firstName',
        <input
          value={form.firstName}
          onChange={(e) => set('firstName', e.target.value)}
          placeholder="Jane"
          className={inputCls}
        />,
        true,
      )}
      {field(
        'Last name',
        'lastName',
        <input
          value={form.lastName}
          onChange={(e) => set('lastName', e.target.value)}
          placeholder="Doe"
          className={inputCls}
        />,
      )}
      {field(
        'Email',
        'email',
        <input
          type="email"
          value={form.email}
          onChange={(e) => set('email', e.target.value)}
          onBlur={blurValidate('email', 'email', validateEmail)}
          placeholder="jane@company.com"
          maxLength={254}
          className={inputCls}
        />,
      )}
      {field(
        'Mobile',
        'phone',
        <input
          type="tel"
          value={form.phone}
          onChange={(e) => set('phone', e.target.value)}
          onBlur={blurValidate('phone', 'mobile number', validateMobile)}
          placeholder="+91 9876543210"
          maxLength={15}
          className={inputCls}
        />,
      )}
    </div>
  );

  const jobStep = (
    <div className="grid gap-3 md:grid-cols-2">
      {field(
        'Designation',
        'designation',
        <input
          value={form.designation}
          onChange={(e) => set('designation', e.target.value)}
          placeholder="Senior Engineer"
          className={inputCls}
        />,
      )}
      {field(
        'Department',
        'department',
        <input
          value={form.department}
          onChange={(e) => set('department', e.target.value)}
          placeholder="Engineering"
          className={inputCls}
        />,
      )}
      {field(
        'Date of joining',
        'dateOfJoining',
        <input
          type="date"
          value={form.dateOfJoining}
          onChange={(e) => set('dateOfJoining', e.target.value)}
          className={inputCls}
        />,
      )}
      {field(
        'Date of leaving',
        'dateOfLeaving',
        <input
          type="date"
          value={form.dateOfLeaving}
          onChange={(e) => set('dateOfLeaving', e.target.value)}
          className={inputCls}
        />,
      )}
      {field(
        'PAN number',
        'panNumber',
        <input
          value={form.panNumber}
          onChange={(e) => set('panNumber', e.target.value.toUpperCase())}
          onBlur={blurValidate('panNumber', 'PAN', validatePan)}
          placeholder="AAAPL1234C"
          maxLength={10}
          className={`${inputCls} font-mono`}
        />,
      )}
      {field(
        'Aadhaar number',
        'aadhaarNumber',
        <input
          value={form.aadhaarNumber}
          onChange={(e) => set('aadhaarNumber', e.target.value)}
          onBlur={blurValidate('aadhaarNumber', 'Aadhaar', validateAadhaar)}
          placeholder="123412341234"
          maxLength={12}
          className={`${inputCls} font-mono`}
        />,
      )}
    </div>
  );

  const orgStep = (
    <div className="grid gap-3 md:grid-cols-2">
      <SearchableSelect
        label="Store *"
        value={form.storeId}
        onChange={(v) => set('storeId', v)}
        loadOptions={loadStoreOptions}
        placeholder="Select a store"
        searchPlaceholder="Search stores..."
        error={errors.storeId}
      />
      <SearchableSelect
        label="Reporting manager"
        value={form.reportingManagerId}
        onChange={(v) => set('reportingManagerId', v)}
        loadOptions={loadEmployeeOptions}
        placeholder="Select a manager"
        searchPlaceholder="Search employees..."
      />
      {/* ImagePicker owns its own label, so render outside `field()`
          and span the row. URL or file upload — both produce a
          single string the form serialises into `imageUrl`. */}
      <div className="md:col-span-2">
        <ImagePicker
          label="Photo"
          value={form.imageUrl}
          onChange={(v) => set('imageUrl', v)}
          hint="Paste a URL or upload a file (≤ 200 KB). Uploads aren't persisted server-side until the upload endpoint ships."
        />
      </div>
      {field(
        'Status',
        'status',
        <select
          value={form.status}
          onChange={(e) => set('status', Number(e.target.value))}
          className={selectCls}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>,
      )}
    </div>
  );

  const reviewStep = (
    <div className="grid gap-3">
      <div className="flex items-center gap-2 text-success">
        <CheckCircle2 className="h-4 w-4" />
        <span className="text-sm font-medium">Ready to save</span>
      </div>
      <div className="rounded-md border border-border bg-surface-card-hover p-4 text-sm">
        <div className="grid gap-2 md:grid-cols-2">
          <Row label="Code" value={form.employeeCode} />
          <Row label="Name" value={`${form.firstName} ${form.lastName}`.trim()} />
          <Row label="Email" value={form.email} />
          <Row label="Mobile" value={form.phone} />
          <Row label="Designation" value={form.designation} />
          <Row label="Department" value={form.department} />
          <Row label="Date of joining" value={form.dateOfJoining} />
          <Row label="Date of leaving" value={form.dateOfLeaving} />
          <Row label="PAN" value={form.panNumber} />
          <Row label="Aadhaar" value={form.aadhaarNumber} />
          <Row label="Store" value={<NameCell kind="store" id={form.storeId} />} />
          <Row label="Reporting manager" value={<NameCell kind="employee" id={form.reportingManagerId} />} />
          <Row
            label="Status"
            value={<StatusBadge status={form.status === 1 ? 'active' : 'inactive'} />}
          />
        </div>
      </div>
    </div>
  );

  const tabs = [
    { id: 'basic' as const, label: STEP_LABELS.basic, content: basicStep },
    { id: 'job' as const, label: STEP_LABELS.job, content: jobStep },
    { id: 'organisation' as const, label: STEP_LABELS.organisation, content: orgStep },
    { id: 'review' as const, label: STEP_LABELS.review, content: reviewStep },
  ];
  const isFirst = STEPS.indexOf(step) === 0;
  const isLast = STEPS.indexOf(step) === STEPS.length - 1;

  return (
    <div className="-m-6 p-[3px]">
      {loadError && <Alert variant="danger" className="mb-3">{loadError}</Alert>}
      <div className="mb-[3px]">
        <Tabs
          tabs={tabs}
          activeId={step}
          onChange={(sid) => {
            const t = STEPS.indexOf(sid as StepId);
            const c = STEPS.indexOf(step);
            if (t < c)
              setRedTabs((p) => {
                const n = new Set(p);
                for (let i = t + 1; i <= c; i++) n.add(STEPS[i]!);
                return n;
              });
            else if (t > c)
              setRedTabs((p) => {
                const n = new Set(p);
                for (let i = c + 1; i <= t; i++) n.delete(STEPS[i]!);
                return n;
              });
            setStep(sid as StepId);
          }}
          tabStatus={tabStatus}
          barOnly
        />
      </div>
      <Card>
        <div className="min-h-[180px]">
          {loading ? <div className="text-sm text-content-muted">Loading…</div> : tabs.find((t) => t.id === step)?.content}
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={goPrev} disabled={isFirst}>
              <ChevronLeft className="h-4 w-4" /> Previous
            </Button>
            {!isLast && (
              <Button type="button" onClick={goNext}>
                Next <ChevronRight className="h-4 w-4" />
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={() => navigate('/panel/employees')}>
              Cancel
            </Button>
            <Button type="button" onClick={handleSave} disabled={!basicValid || saving}>
              <Save className="h-4 w-4" />{' '}
              {saving ? 'Saving…' : isLast ? (isEdit ? 'Update' : 'Save') : 'Next →'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded border border-border bg-surface-card px-3 py-2">
      <span className="text-xs text-content-muted">{label}</span>
      <span className="text-sm font-medium text-content-primary">
        {value !== '' && value != null ? value : <em className="text-content-muted">—</em>}
      </span>
    </div>
  );
}
